import { Course } from '@/types/courseTypes';

export const vuejsAdvancedCourse: Course = {
  id: 'vuejs-advanced',
  title: 'Advanced Vue.js Development',
  description: 'Master advanced Vue.js concepts including Composition API, state management, and performance optimization.',
  level: 'Advanced',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['JavaScript ES6+', 'Vue.js basics', 'Component architecture'],
  learningOutcomes: [
    'Master Vue 3 Composition API',
    'Implement advanced state management with Pinia',
    'Optimize Vue.js application performance',
    'Build scalable component architectures'
  ],
  modules: [
    {
      id: 'vue3-composition-api',
      title: 'Vue 3 Composition API Mastery',
      description: 'Deep dive into Vue 3 Composition API and reactive programming',
      content: `# Vue 3 Composition API Mastery

## Understanding the Composition API Revolution

### Why Composition API?
The Composition API represents a paradigm shift in Vue.js development, offering:
- **Better TypeScript Support**: Native TypeScript integration
- **Improved Code Organization**: Logic grouping by feature
- **Enhanced Reusability**: Composable functions for shared logic
- **Better Performance**: Optimized reactivity system

### Core Composition API Concepts

#### setup() Function
\`\`\`javascript
import { ref, reactive, computed, onMounted } from 'vue'

export default {
  setup() {
    // Reactive state
    const count = ref(0)
    const user = reactive({
      name: 'John',
      email: 'john@example.com'
    })

    // Computed properties
    const doubleCount = computed(() => count.value * 2)

    // Methods
    const increment = () => {
      count.value++
    }

    // Lifecycle hooks
    onMounted(() => {
      console.log('Component mounted')
    })

    // Expose to template
    return {
      count,
      user,
      doubleCount,
      increment
    }
  }
}
\`\`\`

#### Script Setup Syntax
\`\`\`vue
<script setup>
import { ref, computed, onMounted } from 'vue'

// No need for setup() function or return statement
const count = ref(0)
const doubleCount = computed(() => count.value * 2)

const increment = () => {
  count.value++
}

onMounted(() => {
  console.log('Component mounted')
})
</script>

<template>
  <div>
    <p>Count: {{ count }}</p>
    <p>Double: {{ doubleCount }}</p>
    <button @click="increment">Increment</button>
  </div>
</template>
\`\`\`

## Advanced Reactivity Patterns

### Custom Composables
\`\`\`javascript
// composables/useCounter.js
import { ref, computed } from 'vue'

export function useCounter(initialValue = 0) {
  const count = ref(initialValue)
  
  const doubleCount = computed(() => count.value * 2)
  const isEven = computed(() => count.value % 2 === 0)
  
  const increment = () => count.value++
  const decrement = () => count.value--
  const reset = () => count.value = initialValue
  
  return {
    count: readonly(count),
    doubleCount,
    isEven,
    increment,
    decrement,
    reset
  }
}

// Usage in component
<script setup>
import { useCounter } from '@/composables/useCounter'

const { count, doubleCount, increment, decrement } = useCounter(10)
</script>
\`\`\`

### Advanced API Usage
\`\`\`javascript
// composables/useApi.js
import { ref, reactive } from 'vue'

export function useApi() {
  const loading = ref(false)
  const error = ref(null)
  
  const execute = async (apiCall) => {
    loading.value = true
    error.value = null
    
    try {
      const result = await apiCall()
      return result
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }
  
  return {
    loading: readonly(loading),
    error: readonly(error),
    execute
  }
}
\`\`\`

### Reactive References vs Reactive Objects
\`\`\`javascript
import { ref, reactive, toRefs } from 'vue'

// ref() - for primitives and single values
const count = ref(0)
const message = ref('Hello')

// reactive() - for objects and arrays
const state = reactive({
  user: {
    name: 'John',
    email: 'john@example.com'
  },
  items: []
})

// toRefs() - convert reactive object to refs
const { user, items } = toRefs(state)
\`\`\`

## Performance Optimization

### Watch and WatchEffect
\`\`\`javascript
import { ref, watch, watchEffect } from 'vue'

const count = ref(0)
const user = reactive({ name: 'John' })

// watch - explicit dependencies
watch(count, (newValue, oldValue) => {
  console.log(\`Count changed from \${oldValue} to \${newValue}\`)
})

// watchEffect - automatic dependency tracking
watchEffect(() => {
  console.log(\`Current count: \${count.value}\`)
  console.log(\`User name: \${user.name}\`)
})

// Watch multiple sources
watch([count, () => user.name], ([newCount, newName], [oldCount, oldName]) => {
  console.log('Multiple values changed')
})
\`\`\`

### Computed Properties with Getters and Setters
\`\`\`javascript
import { ref, computed } from 'vue'

const firstName = ref('John')
const lastName = ref('Doe')

const fullName = computed({
  get() {
    return \`\${firstName.value} \${lastName.value}\`
  },
  set(value) {
    const [first, last] = value.split(' ')
    firstName.value = first
    lastName.value = last
  }
})
\`\`\``,
      order: 1,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Master Composition API patterns',
        'Understand Vue 3 reactivity system',
        'Create custom composables'
      ]
    },
    {
      id: 'vue-state-management',
      title: 'Advanced State Management',
      description: 'Complex state management with Pinia and advanced patterns',
      content: 'Learn advanced state management patterns, Pinia store design, and complex data flows.',
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Design scalable Pinia stores',
        'Implement complex state patterns',
        'Handle asynchronous state updates'
      ]
    },
    {
      id: 'vue-performance-optimization',
      title: 'Performance Optimization',
      description: 'Vue.js performance optimization techniques and tools',
      content: 'Optimize Vue applications with lazy loading, code splitting, and performance monitoring.',
      order: 3,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement code splitting strategies',
        'Optimize rendering performance',
        'Use Vue DevTools for performance analysis'
      ]
    },
    {
      id: 'vue-enterprise-patterns',
      title: 'Enterprise Architecture Patterns',
      description: 'Scalable architecture patterns for large Vue.js applications',
      content: 'Build enterprise-grade Vue applications with proper architecture and design patterns.',
      order: 4,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Design modular application architecture',
        'Implement design patterns in Vue',
        'Create maintainable component libraries'
      ]
    }
  ]
};

export const angularAdvancedCourse: Course = {
  id: 'angular-advanced',
  title: 'Advanced Angular Development',
  description: 'Advanced Angular development with RxJS, state management, and enterprise patterns.',
  level: 'Advanced',
  totalDuration: '10 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['TypeScript', 'Angular basics', 'RxJS fundamentals'],
  learningOutcomes: [
    'Master advanced RxJS operators and patterns',
    'Implement NgRx for complex state management',
    'Build scalable Angular architectures',
    'Optimize Angular application performance'
  ],
  modules: [
    {
      id: 'rxjs-advanced-patterns',
      title: 'Advanced RxJS Patterns',
      description: 'Complex reactive programming with RxJS',
      content: 'Master advanced RxJS operators, error handling, and reactive patterns.',
      order: 1,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Use advanced RxJS operators',
        'Implement error handling strategies',
        'Create custom operators'
      ]
    },
    {
      id: 'ngrx-state-management',
      title: 'NgRx State Management',
      description: 'Complex state management with NgRx',
      content: 'Implement enterprise-grade state management using NgRx Store, Effects, and Entity.',
      order: 2,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design NgRx store architecture',
        'Implement effects and side effects',
        'Use NgRx Entity for data management'
      ]
    },
    {
      id: 'angular-architecture-patterns',
      title: 'Angular Architecture Patterns',
      description: 'Scalable architecture patterns for large Angular applications',
      content: 'Build maintainable Angular applications with proper architecture and design patterns.',
      order: 3,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement micro-frontend architecture',
        'Design feature modules',
        'Create shared component libraries'
      ]
    },
    {
      id: 'angular-performance-optimization',
      title: 'Performance Optimization',
      description: 'Angular performance optimization and monitoring',
      content: 'Optimize Angular applications with OnPush strategy, lazy loading, and performance monitoring.',
      order: 4,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement OnPush change detection',
        'Optimize bundle size and loading',
        'Set up performance monitoring'
      ]
    }
  ]
};

export const reactAdvancedCourse: Course = {
  id: 'react-advanced-patterns',
  title: 'Advanced React Patterns & Performance',
  description: 'Master advanced React patterns, performance optimization, and modern development practices.',
  level: 'Advanced',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['React fundamentals', 'JavaScript ES6+', 'React Hooks'],
  learningOutcomes: [
    'Implement advanced React patterns',
    'Optimize React application performance',
    'Master React Server Components',
    'Build scalable component architectures'
  ],
  modules: [
    {
      id: 'react-advanced-patterns',
      title: 'Advanced Component Patterns',
      description: 'Complex component patterns and design principles',
      content: 'Learn render props, compound components, and advanced pattern implementations.',
      order: 1,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement compound component patterns',
        'Use render props and HOCs effectively',
        'Create flexible component APIs'
      ]
    },
    {
      id: 'react-performance-optimization',
      title: 'Performance Optimization',
      description: 'React performance optimization techniques',
      content: 'Master React.memo, useMemo, useCallback, and advanced optimization strategies.',
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Optimize re-rendering performance',
        'Use React Profiler effectively',
        'Implement code splitting strategies'
      ]
    },
    {
      id: 'react-server-components',
      title: 'React Server Components',
      description: 'Modern React with Server Components and Suspense',
      content: 'Learn React Server Components, Suspense, and modern React architecture patterns.',
      order: 3,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement React Server Components',
        'Use Suspense for data fetching',
        'Understand streaming and concurrent features'
      ]
    },
    {
      id: 'react-enterprise-architecture',
      title: 'Enterprise React Architecture',
      description: 'Scalable React architecture for large applications',
      content: 'Design enterprise-grade React applications with proper architecture patterns.',
      order: 4,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Design scalable component architectures',
        'Implement state management strategies',
        'Create maintainable project structures'
      ]
    }
  ]
};

export const FRONTEND_ADVANCED_COURSES = [
  vuejsAdvancedCourse,
  angularAdvancedCourse,
  reactAdvancedCourse
];